import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { flashcardService } from "@/services/flashcardService";
import { gamificationService } from "@/services/gamificationService";
import { personalizationService, type CognitiveItem } from "@/services/personalizationService";
import { chatThreadsService } from "@/services/chatThreadsService";
import { knowledgeBaseService } from "@/services/knowledgeBaseService";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import {
  Brain,
  Lightbulb,
  BookOpen,
  HelpCircle,
  Play,
  Check,
  X,
  Volume2,
  VolumeX,
  Trophy,
  ArrowLeft,
  RefreshCw,
  MessageSquarePlus,
  Flame,
  Dumbbell,
  Bookmark,
  ExternalLink,
  Target,
  Star,
} from "lucide-react";

type ContentType = "flashcard" | "did_you_know" | "faq" | "quiz";

interface WorkoutItem {
  id: string;
  question: string;
  answer: string;
  type: ContentType;
  topic?: string;
  source?: string;
  lastShownAt?: number;
  difficulty?: number;
  timesShown?: number;
  timesCorrect?: number;
}

const WITTY_TAGLINES = [
  "Remember that stuff you looked up yesterday? Let's make it stick.",
  "Your brain called. It wants a challenge.",
  "Mental reps for mental gains.",
  "Building neurons, one question at a time.",
  "Knowledge is power. Let's power up.",
  "Your daily dose of 'Aha!' moments.",
  "Flex those brain muscles!",
  "Making connections your future self will thank you for.",
];

const TYPE_CONFIG = {
  flashcard: { label: "Flashcard", icon: BookOpen, color: "#3b82f6", bgColor: "bg-blue-500/10" },
  did_you_know: { label: "Did You Know?", icon: Lightbulb, color: "#eab308", bgColor: "bg-yellow-500/10" },
  faq: { label: "Quick Q&A", icon: HelpCircle, color: "#22c55e", bgColor: "bg-green-500/10" },
  quiz: { label: "Brain Teaser", icon: Brain, color: "#8b5cf6", bgColor: "bg-purple-500/10" },
};

const FRESHNESS_KEY = "brain_builder_freshness";
const MEMORIES_DOC_KEY = "brain_builder_memories_doc_id";
const SESSION_LENGTH = 10;

const getStoredFreshness = (): Record<string, { lastShownAt: number; difficulty: number; timesShown: number; timesCorrect: number }> => {
  try {
    const stored = localStorage.getItem(FRESHNESS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const updateFreshness = (itemId: string, wasCorrect: boolean) => {
  const freshness = getStoredFreshness();
  const existing = freshness[itemId] || { lastShownAt: 0, difficulty: 2, timesShown: 0, timesCorrect: 0 };
  
  freshness[itemId] = {
    lastShownAt: Date.now(),
    difficulty: wasCorrect ? Math.max(1, existing.difficulty - 0.5) : Math.min(5, existing.difficulty + 1),
    timesShown: existing.timesShown + 1,
    timesCorrect: existing.timesCorrect + (wasCorrect ? 1 : 0),
  };
  
  localStorage.setItem(FRESHNESS_KEY, JSON.stringify(freshness));
};

const sortByFreshness = (items: WorkoutItem[]): WorkoutItem[] => {
  const freshness = getStoredFreshness();
  
  return [...items].sort((a, b) => {
    const aData = freshness[a.id];
    const bData = freshness[b.id];
    
    if (!aData && bData) return -1;
    if (aData && !bData) return 1;
    if (!aData && !bData) {
      return a.id.localeCompare(b.id);
    }
    
    const aScore = (aData.difficulty || 2) * 1000 - (aData.lastShownAt || 0) / 100000;
    const bScore = (bData.difficulty || 2) * 1000 - (bData.lastShownAt || 0) / 100000;
    
    return bScore - aScore;
  });
};

const interleaveContent = (items: WorkoutItem[]): WorkoutItem[] => {
  const byType: Record<ContentType, WorkoutItem[]> = {
    flashcard: [],
    did_you_know: [],
    faq: [],
    quiz: [],
  };
  
  items.forEach(item => {
    byType[item.type]?.push({ ...item });
  });
  
  const typeIndices: Record<ContentType, number> = {
    flashcard: 0,
    did_you_know: 0,
    faq: 0,
    quiz: 0,
  };
  
  const interleaved: WorkoutItem[] = [];
  const typeOrder: ContentType[] = ["flashcard", "quiz", "did_you_know", "faq"];
  let typeIndex = 0;
  let iterations = 0;
  const maxIterations = items.length * 4;
  
  while (interleaved.length < items.length && iterations < maxIterations) {
    const type = typeOrder[typeIndex % typeOrder.length];
    const pool = byType[type];
    const idx = typeIndices[type];
    
    if (idx < pool.length) {
      interleaved.push(pool[idx]);
      typeIndices[type]++;
    }
    typeIndex++;
    iterations++;
    
    const allEmpty = Object.entries(byType).every(([t, arr]) => typeIndices[t as ContentType] >= arr.length);
    if (allEmpty) break;
  }
  
  return interleaved;
};

export function BrainBuilderWorkout() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  
  const [isActive, setIsActive] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [sessionItems, setSessionItems] = useState<WorkoutItem[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [memoriesDocId, setMemoriesDocId] = useState<string | null>(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  useEffect(() => {
    const storedDocId = localStorage.getItem(`${MEMORIES_DOC_KEY}_${user?.id}`);
    if (storedDocId) {
      setMemoriesDocId(storedDocId);
    }
  }, [user?.id]);

  useEffect(() => {
    setTaglineIndex(Math.floor(Math.random() * WITTY_TAGLINES.length));
  }, []);

  const { data: gameStats } = useQuery({
    queryKey: ["game-stats", user?.id],
    queryFn: () => gamificationService.getStats(user!.id),
    enabled: !!user?.id,
  });

  const { data: flashcards } = useQuery({
    queryKey: ["flashcards-due", user?.id],
    queryFn: () => flashcardService.getDueCards(user!.id, 10),
    enabled: !!user?.id,
  });

  const { data: aiCognitiveItems, isLoading: isLoadingAI } = useQuery({
    queryKey: ["ai-cognitive-items", user?.id],
    queryFn: () => personalizationService.generateCognitiveItems(user!.id, 16),
    enabled: !!user?.id,
    staleTime: 2 * 60 * 60 * 1000,
  });

  const generateSessionItems = useCallback((): WorkoutItem[] => {
    const allItems: WorkoutItem[] = [];
    
    if (flashcards?.length) {
      flashcards.forEach(f => {
        allItems.push({
          id: f.id,
          question: f.front,
          answer: f.back,
          type: "flashcard",
          topic: f.tags?.[0] || "Your Cards",
          source: "Your Flashcards",
        });
      });
    }
    
    if (aiCognitiveItems?.length) {
      aiCognitiveItems.forEach((item: CognitiveItem) => {
        allItems.push({
          id: item.id,
          question: item.question,
          answer: item.answer,
          type: item.type,
          topic: item.topic,
          source: item.source || "Your Research",
        });
      });
    }
    
    if (allItems.length < SESSION_LENGTH) {
      const fallback: WorkoutItem[] = [
        { id: "f1", question: "Spaced Repetition", answer: "A learning technique that involves reviewing information at increasing intervals to optimize memory retention.", type: "flashcard", topic: "Learning Science" },
        { id: "f2", question: "Neuroplasticity", answer: "The brain's ability to reorganize itself by forming new neural connections throughout life.", type: "flashcard", topic: "Neuroscience" },
        { id: "d1", question: "Reading just 20 minutes a day exposes you to 1.8 million words per year!", answer: "This massive vocabulary exposure is linked to improved cognitive function and memory.", type: "did_you_know", topic: "Reading" },
        { id: "d2", question: "Walking increases creative thinking by up to 60%!", answer: "A Stanford study found that walking significantly boosts creative output.", type: "did_you_know", topic: "Creativity" },
        { id: "q1", question: "What learning technique uses increasing time intervals?", answer: "Spaced Repetition", type: "quiz", topic: "Learning" },
        { id: "q2", question: "What is the brain's ability to form new neural connections called?", answer: "Neuroplasticity", type: "quiz", topic: "Neuroscience" },
        { id: "faq1", question: "How does sleep improve memory?", answer: "During sleep, the brain replays and strengthens neural pathways formed during learning.", type: "faq", topic: "Memory" },
        { id: "faq2", question: "What is the Pomodoro Technique?", answer: "A time management method using 25-minute focused work sessions followed by short breaks.", type: "faq", topic: "Productivity" },
        { id: "d3", question: "Your brain uses 20% of your body's energy despite being only 2% of body weight!", answer: "This energy powers roughly 86 billion neurons firing constantly.", type: "did_you_know", topic: "Brain Science" },
        { id: "q3", question: "How many neurons are in the human brain?", answer: "Approximately 86 billion", type: "quiz", topic: "Neuroscience" },
      ];
      allItems.push(...fallback);
    }
    
    const sortedItems = sortByFreshness(allItems);
    const interleavedItems = interleaveContent(sortedItems.slice(0, SESSION_LENGTH * 2));
    
    return interleavedItems.slice(0, SESSION_LENGTH);
  }, [flashcards, aiCognitiveItems]);

  useEffect(() => {
    if (!isLoadingAI) {
      setSessionItems(generateSessionItems());
    }
  }, [isLoadingAI, generateSessionItems]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    if (user?.id) {
      personalizationService.clearCache(user.id);
      queryClient.invalidateQueries({ queryKey: ["ai-cognitive-items", user.id] });
    }
    setTaglineIndex(Math.floor(Math.random() * WITTY_TAGLINES.length));
    setTimeout(() => {
      setSessionItems(generateSessionItems());
      setIsRefreshing(false);
    }, 800);
  };

  const createThreadMutation = useMutation({
    mutationFn: async (topic: string) => {
      const currentItem = sessionItems[currentIndex];
      if (!currentItem || !user?.id) return null;
      
      const thread = await chatThreadsService.createChatThread(
        {
          userId: user.id,
          title: `EXPLORE: ${currentItem.topic || topic}`,
          messages: [{
            role: "user",
            content: `Help me deeply understand and apply "${currentItem.question}" to my life. The key insight is: ${currentItem.answer}. How can I implement this knowledge about ${topic} for practical benefit in my daily life?`
          }],
        },
        currentWorkspaceId ?? null
      );
      return thread;
    },
    onSuccess: (thread) => {
      if (thread?.id) {
        queryClient.invalidateQueries({ queryKey: ['/api/chat-threads'] });
        navigate(`/chat/${thread.id}`);
      }
    },
  });

  const handleExploreMore = () => {
    const currentItem = sessionItems[currentIndex];
    if (!currentItem) return;
    createThreadMutation.mutate(currentItem.topic || "this concept");
  };

  const saveToMemoriesMutation = useMutation({
    mutationFn: async () => {
      const currentItem = sessionItems[currentIndex];
      if (!currentItem || !user?.id) return null;
      
      const dateStr = new Date().toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const newEntry = `\n\n---\n\n## ${dateStr}\n\n**Topic:** ${currentItem.topic || 'General'}\n\n**Question:** ${currentItem.question}\n\n**Insight:** ${currentItem.answer}\n\n**Type:** ${TYPE_CONFIG[currentItem.type].label}`;
      
      let docId = memoriesDocId;
      
      if (!docId) {
        const doc = await knowledgeBaseService.createKnowledgeBaseItem({
          title: "BRAIN BUILDER MEMORIES",
          content: `# Brain Builder Memories\n\nYour personal journal of insights and knowledge gems from Brain Builder Workouts.\n${newEntry}`,
          type: "note",
        }, currentWorkspaceId ?? null);
        if (doc?.id) {
          docId = doc.id;
          setMemoriesDocId(doc.id);
          localStorage.setItem(`${MEMORIES_DOC_KEY}_${user.id}`, doc.id);
        }
        return doc;
      } else {
        const existingDoc = await knowledgeBaseService.getKnowledgeBaseItem(docId);
        if (existingDoc) {
          const updatedContent = (existingDoc.content || '') + newEntry;
          await knowledgeBaseService.updateKnowledgeBaseItem(docId, { content: updatedContent });
          return existingDoc;
        }
      }
      return null;
    },
    onSuccess: () => {
      setShowSaveConfirm(true);
      setTimeout(() => setShowSaveConfirm(false), 2000);
    },
  });

  const speak = (text: string) => {
    if (audioEnabled && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
  };

  const startSession = () => {
    setIsActive(true);
    setCurrentIndex(0);
    setScore({ correct: 0, total: 0 });
    setRevealed(false);
    if (sessionItems[0]) {
      speak(sessionItems[0].question);
    }
  };

  const handleReveal = () => {
    setRevealed(true);
    if (sessionItems[currentIndex]) {
      speak(sessionItems[currentIndex].answer);
    }
  };

  const handleAnswer = async (correct: boolean) => {
    const currentItem = sessionItems[currentIndex];
    if (currentItem) {
      updateFreshness(currentItem.id, correct);
    }
    
    setScore(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      total: prev.total + 1,
    }));
    setRevealed(false);

    if (currentIndex < sessionItems.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setTimeout(() => {
        speak(sessionItems[nextIndex].question);
      }, 300);
    } else {
      await completeSession();
    }
  };

  const completeSession = async () => {
    if (user?.id) {
      const xpEarned = score.correct * 15 + score.total * 5;
      await gamificationService.addXP(user.id, xpEarned, "brain_workout");
      await gamificationService.incrementStat(user.id, "total_workouts_completed");
      await gamificationService.incrementStat(user.id, "total_cards_reviewed", score.total);
      gamificationService.incrementDailyGoal(user.id, "sessions");
      gamificationService.incrementDailyGoal(user.id, "cards", score.total);
      queryClient.invalidateQueries({ queryKey: ["game-stats"] });
    }
    setIsActive(false);
  };

  const resetSession = () => {
    setIsActive(false);
    setCurrentIndex(0);
    setScore({ correct: 0, total: 0 });
    setRevealed(false);
    speechSynthesis.cancel();
  };

  const currentItem = sessionItems[currentIndex];
  const typeInfo = currentItem ? TYPE_CONFIG[currentItem.type] : null;
  const TypeIcon = typeInfo?.icon || Brain;

  const workoutsCompleted = parseInt(gameStats?.totalWorkoutsCompleted || "0", 10);
  const currentStreak = parseInt(gameStats?.currentStreak || "0", 10);

  if (isActive && currentIndex >= sessionItems.length) {
    const percentage = score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0;
    const xpEarned = score.correct * 15 + score.total * 5;
    
    return (
      <Card className="overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500" />
        <CardContent className="py-8">
          <div className="text-center space-y-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center mx-auto animate-pulse">
                <Trophy className="w-12 h-12 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                <Star className="w-6 h-6 text-white" />
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Workout Complete!
              </h3>
              <p className="text-muted-foreground mt-1">You're building a stronger brain every day!</p>
            </div>
            
            <div className="grid grid-cols-3 gap-3 py-4 max-w-sm mx-auto">
              <div className="p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                <div className="text-2xl font-bold text-green-500">{score.correct}/{score.total}</div>
                <div className="text-xs text-muted-foreground">Correct</div>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <div className="text-2xl font-bold text-amber-500">{percentage}%</div>
                <div className="text-xs text-muted-foreground">Score</div>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/20">
                <div className="text-2xl font-bold text-purple-500">+{xpEarned}</div>
                <div className="text-xs text-muted-foreground">XP Earned</div>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 max-w-xs mx-auto">
              <Button onClick={() => { resetSession(); handleRefresh(); startSession(); }} size="lg" className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600">
                <Dumbbell className="w-4 h-4 mr-2" />
                More Brain Gains!
              </Button>
              <Button onClick={resetSession} variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Start
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shrink-0">
                <Dumbbell className="w-4 h-4 text-white" />
              </div>
              <span className="truncate">BRAIN BUILDER WORKOUT</span>
            </CardTitle>
            {!isActive && (
              <p className="text-sm text-muted-foreground mt-1 italic">
                "{WITTY_TAGLINES[taglineIndex]}"
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            {currentStreak > 0 && (
              <Badge variant="secondary" className="gap-1">
                <Flame className="w-3 h-3 text-orange-500" />
                {currentStreak}
              </Badge>
            )}
            {workoutsCompleted > 0 && (
              <Badge variant="secondary" className="gap-1">
                <Target className="w-3 h-3 text-green-500" />
                {workoutsCompleted}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRefresh}
              disabled={isActive || isRefreshing}
              title="Get fresh content"
              data-testid="button-refresh-workout"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <div className="flex items-center gap-1.5">
              <Switch
                id="audio-workout"
                checked={audioEnabled}
                onCheckedChange={setAudioEnabled}
              />
              {audioEnabled ? (
                <Volume2 className="w-4 h-4 text-muted-foreground" />
              ) : (
                <VolumeX className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isActive ? (
          <div className="space-y-4">
            <div className="p-6 rounded-xl border-2 border-dashed bg-gradient-to-br from-purple-500/5 to-pink-500/5 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Ready to Train?</h3>
              <p className="text-sm text-muted-foreground mb-2">
                {SESSION_LENGTH} mixed items: flashcards, facts, Q&A, and quizzes
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Content tailored to your recent research and interests
              </p>
              
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {Object.entries(TYPE_CONFIG).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <Badge key={key} variant="outline" className="gap-1" style={{ borderColor: config.color }}>
                      <Icon className="w-3 h-3" style={{ color: config.color }} />
                      {config.label}
                    </Badge>
                  );
                })}
              </div>
              
              <Button onClick={startSession} size="lg" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600" data-testid="button-start-workout">
                <Play className="w-4 h-4 mr-2" />
                Start Workout ({SESSION_LENGTH} items)
              </Button>
            </div>
            
            {memoriesDocId && (
              <div className="flex items-center justify-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate(`/library/${memoriesDocId}`)}
                  className="text-muted-foreground"
                  data-testid="link-brain-memories"
                >
                  <BookOpen className="w-3 h-3 mr-1" />
                  View Brain Builder Memories
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetSession}
                className="h-8 px-2"
                data-testid="button-back-workout"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Exit
              </Button>
              
              {typeInfo && (
                <Badge className={typeInfo.bgColor} style={{ color: typeInfo.color, borderColor: `${typeInfo.color}40` }}>
                  <TypeIcon className="w-3 h-3 mr-1" />
                  {typeInfo.label}
                </Badge>
              )}
              
              <span className="text-muted-foreground font-medium">
                {currentIndex + 1} / {sessionItems.length}
              </span>
              
              <div className="flex items-center gap-1">
                <Check className="w-4 h-4 text-green-500" />
                <span className="font-medium text-green-500">{score.correct}</span>
              </div>
            </div>

            <Progress 
              value={((currentIndex + 1) / sessionItems.length) * 100} 
              className="h-2"
            />

            {currentItem && (
              <div 
                className={`min-h-[200px] p-6 rounded-xl border-2 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 ${revealed ? 'bg-gradient-to-br from-green-500/5 to-emerald-500/5' : 'hover:shadow-lg hover:border-purple-300'}`}
                onClick={() => !revealed && handleReveal()}
                style={{ borderColor: revealed ? typeInfo?.color : undefined }}
                data-testid="card-workout-item"
              >
                {currentItem.topic && (
                  <Badge variant="outline" className="mb-3 text-xs">
                    {currentItem.topic}
                  </Badge>
                )}
                <p className="text-lg font-medium mb-2">{currentItem.question}</p>
                
                {revealed && (
                  <div className="mt-4 pt-4 border-t w-full animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <p className="text-muted-foreground">{currentItem.answer}</p>
                    {currentItem.source && (
                      <p className="text-xs text-muted-foreground/60 mt-2">Source: {currentItem.source}</p>
                    )}
                  </div>
                )}
                
                {!revealed && (
                  <p className="text-xs text-muted-foreground mt-4 animate-pulse">
                    Tap to reveal answer
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              {!revealed ? (
                <div className="flex gap-2">
                  <Button 
                    onClick={handleReveal} 
                    className="flex-[2]"
                    data-testid="button-reveal-answer"
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Reveal Answer
                  </Button>
                  <Button 
                    onClick={handleExploreMore}
                    variant="secondary"
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white"
                    disabled={createThreadMutation.isPending}
                    title="Explore this topic deeper in a chat"
                    data-testid="button-explore-deeper"
                  >
                    <MessageSquarePlus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Explore</span>
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => handleAnswer(false)} 
                      className="flex-1 border-red-200 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                      data-testid="button-needs-review"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Needs Work
                    </Button>
                    <Button 
                      onClick={() => handleAnswer(true)} 
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      data-testid="button-got-it"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Got It!
                    </Button>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => saveToMemoriesMutation.mutate()}
                      disabled={saveToMemoriesMutation.isPending}
                      className="flex-1 text-muted-foreground"
                      data-testid="button-save-memory"
                    >
                      {showSaveConfirm ? (
                        <>
                          <Check className="w-4 h-4 mr-1 text-green-500" />
                          Saved!
                        </>
                      ) : (
                        <>
                          <Bookmark className="w-4 h-4 mr-1" />
                          Save to Memories
                        </>
                      )}
                    </Button>
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={handleExploreMore}
                      disabled={createThreadMutation.isPending}
                      className="flex-1 text-muted-foreground"
                      data-testid="button-explore-revealed"
                    >
                      <MessageSquarePlus className="w-4 h-4 mr-1" />
                      Explore Deeper
                    </Button>
                  </div>
                </>
              )}
            </div>
            
            {memoriesDocId && (
              <div className="text-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate(`/library/${memoriesDocId}`)}
                  className="text-xs text-muted-foreground"
                  data-testid="link-memories-inline"
                >
                  <BookOpen className="w-3 h-3 mr-1" />
                  Brain Builder Memories
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
